import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import * as Api from "@/api";
import LineKeyEditor from "@/app/domain/settings/linekeytemplates/lineKeyEditor/LineKeyEditor";
import lineKeyTemplateReducer from "@/app/domain/settings/linekeytemplates/lineKeyTemplateReducer";
import { NEW_LINE_KEY_TEMPLATE } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import Search from "@/design/components/Search";
import useSave from "@/utils/hooks/useSave";
import useSearch from "@/utils/hooks/useSearch";
import Valkyrie, { viBlf, viPlus, viSpinnerThird, viTrashCan, viXmark } from "@sippy-platform/valkyrie";

import {
  Box,
  Card,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

import AddLineKeyTemplate from "./AddLineKeyTemplate";
import CreateLineKeyTemplate from "./CreateLineKeyTemplate";

function LineKeyTemplateAccount({ lineKeyTemplateAccount, user, dispatch }) {
  const lineKeyTemplateStore = useSelector((state) => state.domain.lineKeyTemplates);

  const [loading, setLoading] = useState(true);
  const accountKey = `${lineKeyTemplateAccount.id ?? ""}:${lineKeyTemplateAccount.template?.id ?? ""}`;
  const [previousAccountKey, setPreviousAccountKey] = useState(accountKey);
  const [deleting, setDeleting] = useState(false);
  const [specs, setSpecs] = useState(null);

  if (accountKey !== previousAccountKey) {
    setPreviousAccountKey(accountKey);
    setLoading(true);
  }

  const { value: lineKeyTemplate, dispatch: lktDispatch } = useSave({
    initialValue: NEW_LINE_KEY_TEMPLATE,
    reducer: lineKeyTemplateReducer,
  });

  // Fetch data
  useEffect(() => {
    if (!lineKeyTemplateAccount.id) {
      return;
    }

    Promise.all([
      Api.getLineKeyTemplate(lineKeyTemplateAccount.template),
      Api.getDeviceById(
        lineKeyTemplateAccount.template.preferredDeviceId || lineKeyTemplateAccount.template.preferredDevice?.id
          ? {
              id:
                lineKeyTemplateAccount.template.preferredDeviceId ||
                lineKeyTemplateAccount.template.preferredDevice?.id,
            }
          : { id: 30 },
      ),
    ]).then((_response) => {
      lktDispatch({ type: "set", value: _response[0].data.lineKeyTemplate });
      setSpecs(_response[1].data.device);
      setLoading(false);
    });
  }, [lineKeyTemplateAccount.id, lineKeyTemplateAccount.template, lktDispatch]);

  // Delete line key
  const deleteLineKeyTemplate = async () => {
    setDeleting(true);

    await Promise.resolve(Api.deleteLineKeyTemplateAccount(lineKeyTemplateAccount))
      .then(() => {
        dispatch({ type: "removeLineKeyTemplateAccount", lineKeyTemplateAccount });

        setDeleting(false);
      })
      .catch(() => setDeleting(false));
  };

  const deviceName = useMemo(
    () =>
      lineKeyTemplateAccount.position === 0 ? "Primary device" : `Expansion module #${lineKeyTemplateAccount.position}`,
    [lineKeyTemplateAccount.position],
  );

  // Line key template drawer
  const [drawer, setDrawer] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const searchResults = useSearch(lineKeyTemplateStore.list, searchValue, ["name"]);

  const closeDrawer = useCallback(() => {
    setDrawer(undefined);
    setSearchValue("");
  }, []);

  return (
    <>
      <Card sx={{ position: "relative", contain: "paint" }} variant="outlined">
        {lineKeyTemplateAccount.id ? (
          loading ? (
            <List>
              <ListItem secondaryAction={<Skeleton variant="circular" width={24} height={24} />}>
                <ListItemAvatar>
                  <Skeleton variant="circular" width={32} height={32} />
                </ListItemAvatar>
                <ListItemText
                  disableTypography
                  primary={<Skeleton height={18} width="60%" />}
                  secondary={<Skeleton height={12} width="30%" />}
                />
              </ListItem>
            </List>
          ) : (
            <LineKeyEditor
              key={lineKeyTemplate.id}
              lineKeyTemplate={lineKeyTemplate}
              lineKeyTemplateDispatch={lktDispatch}
              specs={specs}
              customHeader={
                <List>
                  <ListItem
                    disablePadding
                    secondaryAction={
                      <IconButton onClick={deleteLineKeyTemplate} color="inherit">
                        <Valkyrie icon={deleting ? viSpinnerThird : viTrashCan} spin={deleting} />
                      </IconButton>
                    }
                  >
                    <ListItemButton onClick={() => setDrawer(true)} sx={{ borderRadius: 0 }}>
                      <ListItemAvatar>
                        <Avatar text={lineKeyTemplateAccount.template.name} icon={viBlf} />
                      </ListItemAvatar>

                      <ListItemText
                        primary={lineKeyTemplateAccount.template.name}
                        secondary={
                          lineKeyTemplateAccount.position === 0
                            ? "Primary device"
                            : `Expansion module #${lineKeyTemplateAccount.position}`
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                </List>
              }
            />
          )
        ) : (
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setDrawer(true)}>
                <ListItemIcon sx={{ width: 44, pl: 0, pr: 1.5, justifyContent: "center" }}>
                  <Valkyrie icon={viPlus} />
                </ListItemIcon>

                <ListItemText
                  primary={
                    lineKeyTemplateAccount.position === 0
                      ? "Primary device"
                      : `Expansion module #${lineKeyTemplateAccount.position}`
                  }
                />
              </ListItemButton>
            </ListItem>
          </List>
        )}
      </Card>
      <Drawer
        open={!!drawer}
        onClose={() => closeDrawer()}
        anchor="right"
        slotProps={{
          paper: {
            sx: (theme) => ({ maxWidth: 320, width: 1, p: 1.5, display: "flex", gap: 1, ...theme.scrollbar }),
            elevation: 4,
          },
        }}
      >
        <Stack
          direction="row"
          sx={{
            gap: 1,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5">{deviceName}</Typography>
          <IconButton onClick={() => closeDrawer()} color="inherit">
            <Valkyrie icon={viXmark} />
          </IconButton>
        </Stack>
        <Box sx={{ position: "sticky", top: 0, zIndex: 1000 }}>
          <Search blur value={searchValue} onChange={setSearchValue} />
        </Box>
        <List>
          <CreateLineKeyTemplate
            lineKeyTemplateAccount={lineKeyTemplateAccount}
            user={user}
            dispatch={dispatch}
            deviceName={deviceName}
          />
          {searchResults.map((lineKeyTemplate) => (
            <AddLineKeyTemplate
              key={lineKeyTemplate.id}
              lineKeyTemplateAccount={lineKeyTemplateAccount}
              lineKeyTemplate={lineKeyTemplate}
              user={user}
              dispatch={dispatch}
              closeDrawer={closeDrawer}
            />
          ))}
        </List>
      </Drawer>
    </>
  );
}

LineKeyTemplateAccount.propTypes = {
  lineKeyTemplateAccount: PropTypes.object,
  dispatch: PropTypes.func,
};

export default LineKeyTemplateAccount;
